import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../config/api';
import { authTokenService } from './auth.service';

// Tenant portal routes use tenantAuth token; landlord /tenants page must use landlord auth.
const isTenantRoute = () => typeof window !== 'undefined' && (window.location.pathname.startsWith('/tenant/') || window.location.pathname === '/tenant');
const isTenantApiRequest = (url?: string) => url != null && (url.includes('/tenants/') || url.includes('/tenant-'));

class ApiService {
  private api: AxiosInstance;
  // Request deduplication: prevents duplicate simultaneous requests
  private pendingRequests = new Map<string, Promise<any>>();

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor: attach JWT for landlord / platform-admin routes
    this.api.interceptors.request.use(
      (config) => {
        try {
          const useTenantToken = isTenantRoute() && isTenantApiRequest(config.url);
          if (!useTenantToken) {
            const header = authTokenService.getAuthHeaderForUrl(config.url);
            if (header) {
              config.headers.Authorization = header;
            }
          }
          // Tenant routes inject their own token in TenantAuthContext directly via fetch headers
        } catch { /* non-fatal */ }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling (avoid verbose logs for network/CORS)
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
        if (isNetworkError) {
          // CORS / network / backend down - don't log stack; UI will show friendly message
          if (process.env.NODE_ENV === 'development') {
            console.warn('Network request failed (check CORS or backend).');
          }
          return Promise.reject(error);
        }
        if (error.response?.status === 401) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('API 401 Unauthorized');
          }
          return Promise.reject(error);
        }
        // Handle 403 Forbidden errors with permission-specific messages
        if (error.response?.status === 403) {
          const errorData = error.response.data || {};
          const errorCode = errorData.code;

          // ── Property suspended by admin ───────────────────────────────────
          if (errorCode === 'PROPERTY_SUSPENDED') {
            window.dispatchEvent(
              new CustomEvent('property:suspended', {
                detail: {
                  message: errorData.message || 'This property has been suspended by the administrator.',
                },
              })
            );
            return Promise.reject(error);
          }

          // ── Billing gate: subscription expired / past-due ─────────────────
          // Dispatch a global event so ModernLayout can show the billing wall
          // without every individual component needing to handle this case.
          // This event is intentionally NOT fired on tenant routes.
          if (errorCode === 'BILLING_SUSPENDED') {
            window.dispatchEvent(
              new CustomEvent('billing:suspended', {
                detail: {
                  reason: errorData.reason,
                  redirectTo: errorData.redirectTo,
                  message: errorData.message,
                },
              })
            );
            return Promise.reject(error);
          }

          // Special case: email registered as tenant
          if (errorCode === 'EMAIL_REGISTERED_AS_TENANT' && !isTenantRoute()) {
            authTokenService.clearToken();
            const base = typeof window !== 'undefined' ? window.location.origin : '';
            window.location.href = `${base}/tenant/login`;
            return Promise.reject(error);
          }

          // Permission denied errors - enhance error message with permission info
          if (errorCode === 'INSUFFICIENT_PERMISSIONS') {
            const requiredPermission = errorData.requiredPermission;
            const userRole = errorData.userRole;

            // Create a more helpful error message
            const permissionMessages: Record<string, string> = {
              'canCreate': 'You need permission to create resources. Contact your administrator to grant "Create" permission.',
              'canRead': 'You need permission to view resources. Contact your administrator to grant "Read" permission.',
              'canUpdate': 'You need permission to update resources. Contact your administrator to grant "Update" permission.',
              'canDelete': 'You need permission to delete resources. Contact your administrator to grant "Delete" permission.',
              'canManageUsers': 'You need permission to manage users. Contact your administrator to grant "Manage Users" permission.',
              'canManageProperties': 'You need permission to manage properties. Contact your administrator to grant "Manage Properties" permission.',
              'canManageTenants': 'You need permission to manage tenants. Contact your administrator to grant "Manage Tenants" permission.',
              'canManagePayments': 'You need permission to manage payments. Contact your administrator to grant "Manage Payments" permission.',
            };

            const friendlyMessage = permissionMessages[requiredPermission] ||
              `You don't have the required permission: ${requiredPermission}. Your current role is ${userRole || 'unknown'}. Please contact your administrator.`;

            error.permissionError = {
              requiredPermission,
              userRole,
              friendlyMessage,
              originalMessage: errorData.message || 'Permission denied'
            };
          }
        }
        // No account in DB (e.g. user deleted) – sign out and redirect to login so user can sign up
        if (error.response?.status === 404 && error.response?.data?.code === 'USER_NOT_FOUND' && !isTenantRoute()) {
          authTokenService.clearToken();
          const base = typeof window !== 'undefined' ? window.location.origin : '';
          window.location.href = `${base}/login?reason=no_account`;
          return Promise.reject(error);
        }
        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'] || error.response.headers['x-ratelimit-reset'];
          const errorData = error.response.data || {};
          error.rateLimitInfo = {
            retryAfter: retryAfter ? parseInt(retryAfter as string) : null,
            retryAfterSeconds: retryAfter ? parseInt(retryAfter as string) : null,
            message: errorData.error || errorData.message || 'Too many requests. Please try again later.',
            retryAfterText: errorData.retryAfter || 'a few minutes'
          };
          if (process.env.NODE_ENV === 'development') {
            console.warn('API 429 Rate Limit:', error.rateLimitInfo);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic request method
  async request<T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url: string,
    data?: any,
    config?: any
  ): Promise<AxiosResponse<T>> {
    return this.api.request({
      method,
      url,
      data,
      ...config,
    });
  }

  // Convenience methods with request deduplication
  async get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    // Create unique key for request deduplication
    const requestKey = `GET:${url}:${JSON.stringify(config?.params || {})}`;

    // If same request is already pending, return the existing promise
    if (this.pendingRequests.has(requestKey)) {
      // Request deduplicated
      return this.pendingRequests.get(requestKey)!;
    }

    // Create new request
    const request = this.api.get<T>(url, config);
    this.pendingRequests.set(requestKey, request);

    // Clean up after request completes (success or error)
    request.finally(() => {
      this.pendingRequests.delete(requestKey);
    });

    return request;
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.post(url, data, config);
  }

  async getCurrentUser() {
    return this.get('/auth/me');
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.put(url, data, config);
  }

  async delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.api.delete(url, config);
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.patch(url, data, config);
  }

  // Auth methods - These might not be needed with Firebase auth
  async login(credentials: { email: string; password: string }) {
    // This method might not be needed with Firebase auth
    console.warn('login method called - consider using Firebase auth directly');
    return this.post('/auth/login', credentials);
  }

  async logout() {
    // This method might not be needed with Firebase auth
    console.warn('logout method called - consider using Firebase auth directly');
    return this.post('/auth/logout');
  }

  async refreshToken() {
    // This method might not be needed with Firebase auth
    console.warn('refreshToken method called - consider using Firebase auth directly');
    return this.post('/auth/refresh');
  }

  // Property methods
  async getProperties() {
    return this.get('/properties');
  }

  async getProperty(id: string) {
    return this.get(`/properties/${id}`);
  }

  async getPropertyReportAvailablePeriods(id: string) {
    return this.get(`/properties/${id}/reports/available-periods`);
  }

  async getPropertyConsolidatedReport(id: string, month?: number, year?: number) {
    const params = new URLSearchParams();
    if (month) params.set('month', String(month));
    if (year) params.set('year', String(year));
    const qs = params.toString();
    return this.get(`/properties/${id}/reports/consolidated${qs ? `?${qs}` : ''}`);
  }

  async downloadPropertyConsolidatedReportPdf(id: string, month: number, year: number): Promise<Blob> {
    const params = new URLSearchParams({
      month: String(month),
      year: String(year),
    });
    const res = await this.get<Blob>(`/properties/${id}/reports/consolidated/pdf?${params}`, {
      responseType: 'blob',
    });
    return res.data;
  }

  async createProperty(property: any) {
    return this.post('/properties', property);
  }

  async updateProperty(id: string, property: any) {
    return this.put(`/properties/${id}`, property);
  }

  async reGeocodeProperty(id: string) {
    return this.post(`/properties/${id}/regeocode`, {});
  }

  async createPropertyLocationPinLink(id: string) {
    return this.post(`/properties/${id}/location-pin-link`, {});
  }

  async getPropertyLocationPinContext(token: string) {
    return this.get(`/property-location-links/${token}`);
  }

  async submitPropertyLocationPin(token: string, payload: any) {
    return this.post(`/property-location-links/${token}/submit`, payload);
  }

  async deleteProperty(id: string) {
    return this.delete(`/properties/${id}`);
  }

  async getPropertyLeases(propertyId: string) {
    const res = await this.get<{ leases: Array<{ id: string;[key: string]: unknown }> }>(`/properties/${propertyId}/leases`);
    return res.data?.leases ?? [];
  }

  /** Property-wide lease template (when property has no tenants yet). */
  async getPropertyDefaultLeaseContent(propertyId: string) {
    const res = await this.get<{ agreementContent: string | null }>(`/properties/${propertyId}/default-lease-content`);
    return res.data;
  }

  async savePropertyDefaultLeaseContent(propertyId: string, content: string) {
    return this.put(`/properties/${propertyId}/default-lease-content`, { content });
  }

  // Lease methods (landlord)
  async getLease(leaseId: string) {
    return this.get(`/leases/${leaseId}`);
  }

  async saveLeaseDocument(leaseId: string, payload: { title?: string; content: string }) {
    return this.put(`/leases/${leaseId}/document`, payload);
  }

  async sendLeaseForSignature(leaseId: string) {
    return this.put(`/leases/${leaseId}/send-for-signature`, {});
  }

  /** Download saved lease as PDF (same format as tenants see). Only works for leases that have been saved. */
  async getLeaseDocumentDownload(leaseId: string, template?: boolean): Promise<Blob> {
    const url = template ? `/leases/${leaseId}/download?template=true` : `/leases/${leaseId}/download`;
    const res = await this.get<Blob>(url, { responseType: 'blob' });
    return res.data;
  }

  // Tenant methods
  async getTenants(params?: { page?: number; limit?: number; status?: string; search?: string }) {
    return this.get('/tenants', { params });
  }

  async getTenant(id: string) {
    return this.get(`/tenants/${id}`);
  }

  async createTenant(tenant: any) {
    return this.post('/tenants', tenant);
  }

  async updateTenant(id: string, tenant: any) {
    return this.put(`/tenants/${id}`, tenant);
  }

  async updateTenantStatus(id: string, status: string) {
    return this.patch(`/tenants/${id}/status`, { status });
  }

  async deleteTenant(id: string) {
    return this.delete(`/tenants/${id}`);
  }

  async resendDepositLink(tenantId: string) {
    // Send empty body to ensure Content-Type header is included
    return this.post(`/tenants/${tenantId}/resend-deposit-link`, {});
  }

  async bulkDeleteTenants(ids: string[]) {
    // Axios delete with body requires using request method
    return this.request('DELETE', '/tenants/bulk/delete', { ids });
  }

  // Tenant preferences methods
  async getTenantPreferences(tenantId: string) {
    return this.get(`/tenants/${tenantId}/preferences`);
  }

  async updateTenantPreferences(tenantId: string, preferences: any) {
    return this.put(`/tenants/${tenantId}/preferences`, preferences);
  }

  // Unit methods - Note: Units are nested under properties
  async getUnits(propertyId?: string) {
    if (propertyId) {
      return this.get(`/properties/${propertyId}/units`);
    }
    return this.get('/units'); // Fallback for general units endpoint
  }

  async getUnit(id: string) {
    return this.get(`/units/${id}`);
  }

  async createUnit(propertyId: string, unit: any) {
    return this.post(`/properties/${propertyId}/units`, unit);
  }

  async updateUnit(id: string, unit: any) {
    return this.put(`/units/${id}`, unit);
  }

  async deleteUnit(id: string) {
    return this.delete(`/units/${id}`);
  }

  async updateUnitStatus(id: string, status: string) {
    return this.patch(`/units/${id}/status`, { status });
  }

  async synchronizeUnitStatus() {
    return this.post('/units/synchronize');
  }

  // Payment methods
  async getPayments() {
    return this.get('/payments');
  }

  async getPayment(id: string) {
    return this.get(`/payments/${id}`);
  }

  async createPayment(payment: any) {
    return this.post('/payments', payment);
  }

  async updatePayment(id: string, payment: any) {
    return this.put(`/payments/${id}`, payment);
  }

  async deletePayment(id: string) {
    return this.delete(`/payments/${id}`);
  }

  // Template methods
  async getTemplates() {
    return this.get('/templates');
  }

  async getTemplate(id: string) {
    return this.get(`/templates/${id}`);
  }

  async seedDefaultTemplates() {
    return this.post('/templates/seed-defaults', {});
  }

  async createTemplate(template: any) {
    return this.post('/templates', template);
  }

  async updateTemplate(id: string, template: any) {
    return this.put(`/templates/${id}`, template);
  }

  async deleteTemplate(id: string) {
    return this.delete(`/templates/${id}`);
  }

  // Reminder methods
  async getReminders() {
    // Using /reminders/history to get all reminders (past, present, future)
    return this.get('/reminders/history');
  }

  async getReminder(id: string) {
    return this.get(`/reminders/${id}`);
  }

  async createReminder(reminder: any) {
    return this.post('/reminders', reminder);
  }

  async updateReminder(id: string, reminder: any) {
    return this.put(`/reminders/${id}`, reminder);
  }

  async deleteReminder(id: string) {
    return this.delete(`/reminders/${id}`);
  }

  // Announcement methods
  async getAnnouncements() {
    return this.get('/announcements');
  }

  async getAnnouncement(id: string) {
    return this.get(`/announcements/${id}`);
  }

  async createAnnouncement(announcement: any) {
    return this.post('/announcements', announcement);
  }

  async updateAnnouncement(id: string, announcement: any) {
    return this.put(`/announcements/${id}`, announcement);
  }

  async deleteAnnouncement(id: string) {
    return this.delete(`/announcements/${id}`);
  }

  // Additional reminder methods
  async getPendingReminders() {
    return this.get('/reminders/pending');
  }

  async getReminderHistory() {
    return this.get('/reminders/history');
  }

  async getReminderStats() {
    return this.get('/reminders/stats');
  }

  async getAnnouncementStats() {
    return this.get('/announcements/stats');
  }

  async sendReminder(id: string) {
    return this.post(`/reminders/${id}/send`);
  }

  async cancelReminder(id: string) {
    return this.post(`/reminders/${id}/cancel`);
  }

  async retryReminder(id: string) {
    return this.post(`/reminders/${id}/retry`);
  }

  // Financial methods
  async getDashboardStats() {
    return this.get('/financial/dashboard/stats');
  }

  async getFinancialDashboard(propertyId?: string) {
    const params = propertyId ? { propertyId } : undefined;
    return this.get('/financial/dashboard', { params });
  }

  async getCashFlow(propertyId?: string, months: number = 12) {
    const params: any = { months };
    if (propertyId) {
      params.propertyId = propertyId;
    }
    return this.get('/financial/cashflow', { params });
  }

  async getPaymentAnalytics(propertyId?: string) {
    const params = propertyId ? { propertyId } : undefined;
    return this.get('/financial/analytics', { params });
  }

  async getFeeAnalytics(months: number = 1) {
    return this.get('/financial/fees', { params: { months } });
  }

  async getAuditLogs(params?: { category?: string; severity?: string; limit?: number; offset?: number }) {
    return this.get('/audit/logs', { params });
  }

  async getNotificationPreferences() {
    return this.get('/landlord-settings/notifications');
  }

  async updateNotificationPreferences(preferences: any) {
    return this.put('/landlord-settings/notifications', preferences);
  }

  async getPayoutSettings() {
    return this.get('/landlord-settings/payouts/settings');
  }

  async updatePayoutSettings(settings: any) {
    return this.put('/landlord-settings/payouts/settings', settings);
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.put('/landlord-settings/password', { currentPassword, newPassword });
  }

  // Team / auth (invite, users list, invitation accept)
  async inviteUser(payload: { email: string; role: string; phone: string; customRoleId?: string }) {
    return this.post<{ message: string; invitation: any; user: any; emailSent: boolean }>('/auth/invite', payload);
  }

  async getTeamUsers(params?: { page?: number; limit?: number; role?: string; search?: string }) {
    return this.get<{ users: any[]; pagination: any }>('/auth/users', { params });
  }

  async deleteTeamUser(userId: string) {
    return this.delete(`/auth/users/${userId}`);
  }

  async resendTeamInvitation(userId: string) {
    return this.post<{ message: string; invitationUrl?: string; emailSent?: boolean }>(`/auth/users/${userId}/resend-invitation`);
  }

  async updateTeamUserStatus(userId: string, accountStatus: 'ACTIVE' | 'RESTRICTED' | 'SUSPENDED') {
    return this.patch(`/auth/users/${userId}/status`, { accountStatus });
  }

  async updateTeamUser(userId: string, data: { phone?: string; role?: string }) {
    return this.put(`/auth/users/${userId}`, data);
  }

  async getRoles() {
    return this.get<{
      roles: Array<{
        id: string;
        name: string;
        description: string;
        userCount: number;
        type: string;
        createdAt: string;
        permissions: Record<string, boolean>;
      }>
    }>('/auth/roles');
  }

  async updateRolePermissions(roleName: string, permissions: Record<string, boolean>) {
    return this.put(`/auth/roles/${encodeURIComponent(roleName)}/permissions`, { permissions });
  }

  async createRole(payload: { name: string; description?: string; permissions?: Record<string, boolean> }) {
    return this.post<{ role: any }>('/auth/roles', payload);
  }

  async getInvitation(token: string) {
    return this.get<{ invitation: any }>(`/auth/invitation/${token}`);
  }

  async acceptInvitation(token: string, password: string) {
    return this.post<{ message: string; token: string; user: any }>('/auth/accept-invitation', { token, password });
  }
}

export const apiService = new ApiService(); 